/* Copyright 2021, Milkdown by Mirone. */

import { css } from '@emotion/css';
import { CmdKey, commandsCtx, Ctx } from '@milkdown/core';
import { EditorView } from '@milkdown/prose';
import { Utils } from '@milkdown/utils';

import type { MenuCommonConfig } from './config';
import { ManagerOptions } from './manager';

type SelectOptions = {
  id: string;
  text: string;
};

export type SelectConfig<T = any> = {
  type: 'select';
  text: string;
  options: SelectOptions[];
  active?: (view: EditorView) => string;
  onSelect: (id: string, view: EditorView) => [key: CmdKey<T>, info?: T];
  alwaysVisible: boolean;
} & MenuCommonConfig;

export const select = (
  utils: Utils,
  config: SelectConfig,
  ctx: Ctx,
  view: EditorView,
  options: ManagerOptions
) => {
  const selectStyle = utils.getStyle((themeTool) => {
    return css`
      flex-shrink: 0;
      font-weight: 500;
      font-size: 0.875rem;
      ${themeTool.mixin.border('right')};
      ${themeTool.mixin.border('left')};
      .menu-selector {
        border: 0;
        box-sizing: unset;
        cursor: pointer;
        font: inherit;
        text-align: left;
        justify-content: space-between;
        align-items: center;
        color: ${themeTool.palette('neutral', 0.87)};
        display: flex;
        padding: 0.25rem 0.5rem;
        margin: 0.5rem;
        background: ${themeTool.palette('secondary', 0.12)};
        width: 10.375rem;
        &:disabled {
          display: none;
        }
      }
      .menu-selector-value {
        flex: 1;
        white-space: nowrap;
        text-overflow: ellipsis;
      }
      .menu-selector-list {
        width: calc(12.375rem);
        position: absolute;
        top: 3rem;
        background: ${themeTool.palette('surface')};
        ${themeTool.mixin.border()};
        ${themeTool.mixin.shadow()};
        border-bottom-left-radius: ${themeTool.size.radius};
        border-bottom-right-radius: ${themeTool.size.radius};
        z-index: 3;
      }
      .menu-selector-list-item {
        background-color: transparent;
        border: 0;
        cursor: pointer;
        display: block;
        font: inherit;
        text-align: left;
        padding: 0.75rem 1rem;
        line-height: 1.5rem;
        width: 100%;
        color: ${themeTool.palette('neutral', 0.87)};
        &:hover {
          background: ${themeTool.palette('secondary', 0.12)};
          color: ${themeTool.palette('primary')};
        }
      }
      &.fold {
        border-color: transparent;
        .menu-selector {
          background: unset;
        }
        .menu-selector-list {
          display: none;
        }
      }
    `;
  });

  const selectorWrapper = document.createElement('div');
  selectorWrapper.classList.add('menu-selector-wrapper', 'fold');

  const selector = document.createElement('button');
  selector.setAttribute('type', 'button');
  selector.classList.add('menu-selector', 'fold');
  selector.addEventListener('mousedown', (e) => {
    e.preventDefault();
    e.stopPropagation();
    selectorWrapper.classList.toggle('fold');
    selectorList.style.left = `${
      selectorWrapper.getBoundingClientRect().left -
      view.dom.getBoundingClientRect().left
    }px`;
  });
  view.dom.addEventListener('click', () => {
    selectorWrapper.classList.add('fold');
  });

  const selectorValue = document.createElement('span');
  selectorValue.classList.add('menu-selector-value');
  selectorValue.textContent = config.text;

  const selectorButton = utils.themeTool.slots.icon('downArrow');
  selectorButton.setAttribute('aria-hidden', 'true');

  selectorWrapper.appendChild(selector);
  selector.appendChild(selectorValue);
  selector.appendChild(selectorButton);

  const selectorList = document.createElement('div');
  selectorList.classList.add('menu-selector-list');
  config.options.forEach((option) => {
    const selectorListItem = document.createElement('button');
    selectorListItem.setAttribute('type', 'button');
    selectorListItem.dataset.id = option.id;
    selectorListItem.textContent = option.text;
    selectorListItem.classList.add('menu-selector-list-item');
    selectorList.appendChild(selectorListItem);
  });

  selectorList.addEventListener('mousedown', (e) => {
    const { target } = e;
    if (target instanceof HTMLButtonElement && target.dataset.id) {
      ctx.get(commandsCtx).call(...config.onSelect(target.dataset.id, view));
      selectorWrapper.classList.add('fold');
    }
  });

  selectorWrapper.appendChild(selectorList);

  if (selectStyle) {
    selectorWrapper.classList.add(selectStyle);
  }

  if (config.alwaysVisible) {
    selector.removeAttribute('disabled');
    return selectorWrapper;
  }

  const disabled =
    !options.enabled || (config.disabled && config.disabled(view));
  if (disabled) {
    selector.classList.add('disabled');
    selector.children[0].setAttribute('disabled', 'true');
  } else {
    selector.classList.remove('disabled');
    selector.children[0].removeAttribute('disabled');
  }

  return selectorWrapper;
};
